import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import ProjectAgentAssignments, {
  type ProjectAgentInput,
} from '@/frontend/features/Fde/ProjectAgentAssignments'
import ProjectIdentityFields from '@/frontend/features/Fde/ProjectIdentityFields'
import type { Agent, FdeCopy, Project } from '@/frontend/features/Fde/types'
import { fdeRoutes } from '@/frontend/features/Fde/routes'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  project: Project | null
  agents: Agent[]
  copy: FdeCopy
  activeProjectId?: string | null
  returnTo?: 'management' | 'settings'
}

export default function ProjectDialog({
  open,
  onOpenChange,
  project,
  agents,
  copy,
  activeProjectId,
  returnTo,
}: Props) {
  const form = useForm<{
    record_id: string
    task_prefix: string
    name: string
    description: string
    icon: string
    logo: File | null
    remove_logo: boolean
    objective: string
    repository_remote_url: string
    repository_branch: string
    agent_assignments: ProjectAgentInput[]
  }>({
    record_id: '',
    task_prefix: '',
    name: '',
    description: '',
    icon: 'building_2',
    logo: null,
    remove_logo: false,
    objective: '',
    repository_remote_url: '',
    repository_branch: 'main',
    agent_assignments: [],
  })

  useEffect(() => {
    if (!open) return
    form.setData({
      record_id: project?.id ?? window.crypto.randomUUID(),
      task_prefix: project?.task_prefix ?? '',
      name: project?.name ?? '',
      description: project?.description ?? '',
      icon: project?.icon ?? 'building_2',
      logo: null,
      remove_logo: false,
      objective: project?.objective ?? '',
      repository_remote_url: project?.repository_remote_url ?? '',
      repository_branch: project?.repository_branch ?? 'main',
      agent_assignments:
        project?.agent_assignments.map((assignment) => ({
          digital_worker_id: assignment.agent.id,
          role_name: assignment.role_name,
          specialist_kind: assignment.specialist_kind,
          primary: assignment.primary,
        })) ?? agents.flatMap((agent) => agent.default_project_assignment ? [agent.default_project_assignment] : []),
    })
  }, [open, project?.id])

  const submit = () => {
    const options = { onSuccess: () => onOpenChange(false), preserveScroll: true }
    const query = new URLSearchParams()
    if (activeProjectId) query.set('project_id', activeProjectId)
    if (returnTo) query.set('return_to', returnTo)
    const suffix = query.size > 0 ? `?${query.toString()}` : ''
    const multipartOptions = { ...options, forceFormData: Boolean(form.data.logo || form.data.remove_logo) }
    form.transform((data) => ({ project: data }))
    if (project) form.patch(`${fdeRoutes.projects}/${project.id}${suffix}`, multipartOptions)
    else form.post(`${fdeRoutes.projects}${suffix}`, multipartOptions)
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={project ? copy.workspace.edit_project : copy.workspace.new_project}
      description={!project ? copy.workspace.new_project_description : undefined}
      closeLabel={copy.common.close}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {copy.common.cancel}
          </Button>
          <Button disabled={form.processing || !form.data.name.trim()} onClick={submit}>
            {copy.common.save}
          </Button>
        </>
      }
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <TextControl
          label={copy.forms.name}
          value={form.data.name}
          onChange={(e) => form.setData('name', e.target.value)}
          required
        />
        <div>
          <TextControl
            label={copy.management.task_prefix}
            value={form.data.task_prefix}
            onChange={(e) => form.setData('task_prefix', e.target.value.toUpperCase())}
            disabled={project?.task_prefix_locked}
            maxLength={8}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {project?.task_prefix_locked
              ? copy.management.task_prefix_locked
              : copy.management.task_prefix_hint}
          </p>
        </div>
        <TextControl
          label={copy.forms.repository_branch}
          value={form.data.repository_branch}
          onChange={(e) => form.setData('repository_branch', e.target.value)}
          required
        />
        <div className="sm:col-span-2">
          <TextareaControl
            label={copy.forms.description}
            value={form.data.description}
            onChange={(e) => form.setData('description', e.target.value)}
            rows={2}
          />
        </div>
        <div className="sm:col-span-2">
          <ProjectIdentityFields
            name={form.data.name}
            icon={form.data.icon}
            logoUrl={project?.logo_url || null}
            logo={form.data.logo}
            removed={form.data.remove_logo}
            copy={copy}
            onIcon={(icon) => form.setData('icon', icon)}
            onLogo={(logo, removeLogo) =>
              form.setData((data) => ({ ...data, logo, remove_logo: removeLogo }))
            }
          />
        </div>
        <div className="sm:col-span-2">
          <TextareaControl
            label={copy.forms.objective}
            value={form.data.objective}
            onChange={(e) => form.setData('objective', e.target.value)}
            rows={4}
          />
        </div>
        <div className="sm:col-span-2">
          <TextControl
            label={copy.forms.repository_remote_url}
            value={form.data.repository_remote_url}
            onChange={(e) => form.setData('repository_remote_url', e.target.value)}
            placeholder={copy.forms.repository_remote_example}
          />
          <p className="mt-1 text-xs text-muted-foreground">{copy.forms.repository_remote_url_hint}</p>
        </div>
        <div className="sm:col-span-2">
          <ProjectAgentAssignments
            agents={agents}
            assignments={form.data.agent_assignments}
            savedAssignments={project?.agent_assignments ?? []}
            copy={copy}
            onChange={(assignments) => form.setData('agent_assignments', assignments)}
          />
        </div>
      </div>
    </FdeEditorDialog>
  )
}
